import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import {
  Diamond,
  Trophy,
  TrendingUp,
  TrendingDown,
  Target,
  Shield,
  RefreshCw,
  Check,
  Clock,
  Zap,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const TIER_COLORS = {
  purple: "border-purple-500/40 bg-purple-500/5 text-purple-300",
  emerald: "border-emerald-500/40 bg-emerald-500/5 text-emerald-300",
  blue: "border-blue-500/40 bg-blue-500/5 text-blue-300",
  amber: "border-amber-500/40 bg-amber-500/5 text-amber-300",
};

function StarRow({ count }) {
  return (
    <div className="flex gap-0.5">
      {[...Array(5)].map((_, i) => (
        <span
          key={i}
          className={i < count ? "text-yellow-400" : "text-gray-700"}
        >
          ★
        </span>
      ))}
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function EmergentPromise() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [takingPick, setTakingPick] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get("/emergent-promise");
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await api.post("/emergent-promise/refresh");
      toast.success("Scoring fresh candidates…");
      setTimeout(fetchData, 30000);
    } catch {
      toast.error("Refresh failed");
    } finally {
      setTimeout(() => setRefreshing(false), 30000);
    }
  };

  const handleTakePick = async (pick) => {
    setTakingPick(pick.pick_id);
    try {
      // 1. Mark as taken (track record)
      await api.post(`/emergent-promise/take/${pick.pick_id}`);
      // 2. Auto-open paper trade
      try {
        const side = pick.action === "BUY PUTS" ? "SHORT" : "LONG";
        await api.post("/paper-portfolio/trades", {
          symbol: pick.symbol,
          side,
          qty: 100,
          entry_price: pick.entry_price,
          stop: pick.stop_price,
          target: pick.target_price,
          source: "emergent_promise",
          source_id: pick.pick_id,
          notes: `${pick.tier?.label || "Promise"} · score ${pick.score}`,
        });
      } catch (paperErr) {
        // Don't fail the take if paper-trade fails
        console.warn("paper-trade open failed:", paperErr);
      }
      toast.success(`✓ ${pick.symbol} taken — paper trade opened in Paper Portfolio`);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark");
    } finally {
      setTakingPick(null);
    }
  };

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 120000);
    return () => clearInterval(t);
  }, [fetchData]);

  const picks = data?.open_picks || [];
  const tr = data?.track_record || {};

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="emergent-promise-page">
      {/* Hero / track record */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight flex items-center gap-3">
            <Diamond className="w-8 h-8 text-purple-400" />
            EMERGENT PROMISE
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            High-conviction trades · multi-source convergence · independent paper engine
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          className="gap-2"
          data-testid="ep-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Scoring…" : "Re-score"}
        </Button>
      </div>

      {/* Track record bar */}
      <Card className="card-dark border-purple-500/30 bg-gradient-to-r from-purple-500/5 to-emerald-500/5">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="w-5 h-5 text-yellow-400" />
            <h2 className="font-heading font-bold text-lg tracking-tight">TRACK RECORD</h2>
            <span className="text-xs text-muted-foreground ml-auto">
              {tr.open_count || 0} open · {tr.closed_count || 0} resolved
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="text-center p-2 rounded bg-black/30">
              <p className="text-[10px] text-muted-foreground uppercase">Win Rate</p>
              <p className={`text-2xl font-bold ${tr.win_rate_pct >= 60 ? "text-emerald-400" : tr.win_rate_pct >= 40 ? "text-amber-400" : "text-red-400"}`}>
                {tr.win_rate_pct || 0}%
              </p>
            </div>
            <div className="text-center p-2 rounded bg-black/30">
              <p className="text-[10px] text-muted-foreground uppercase">Avg Return</p>
              <p className={`text-2xl font-bold ${(tr.avg_return_pct || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                {(tr.avg_return_pct || 0) >= 0 ? "+" : ""}{tr.avg_return_pct || 0}%
              </p>
            </div>
            <div className="text-center p-2 rounded bg-black/30">
              <p className="text-[10px] text-muted-foreground uppercase">Wins</p>
              <p className="text-2xl font-bold text-emerald-400">{tr.wins || 0}</p>
              <p className="text-[10px] text-emerald-400">avg +{tr.avg_win_pct || 0}%</p>
            </div>
            <div className="text-center p-2 rounded bg-black/30">
              <p className="text-[10px] text-muted-foreground uppercase">Losses</p>
              <p className="text-2xl font-bold text-red-400">{tr.losses || 0}</p>
              <p className="text-[10px] text-red-400">avg {tr.avg_loss_pct || 0}%</p>
            </div>
            <div className="text-center p-2 rounded bg-black/30">
              <p className="text-[10px] text-muted-foreground uppercase">Total Calls</p>
              <p className="text-2xl font-bold text-white">{tr.total_picks || 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Open picks */}
      <div>
        <h2 className="font-heading font-bold text-lg tracking-tight mb-3 flex items-center gap-2">
          <Zap className="w-5 h-5 text-yellow-400" />
          ACTIVE PROMISES
          <span className="text-sm text-muted-foreground font-normal ml-2">
            ({picks.length})
          </span>
        </h2>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-64 skeleton rounded-sm" />
            ))}
          </div>
        ) : picks.length === 0 ? (
          <Card className="card-dark border-gray-700">
            <CardContent className="p-8 text-center">
              <Diamond className="w-12 h-12 text-gray-500 mx-auto mb-3" />
              <p className="text-muted-foreground">No high-conviction setups right now.</p>
              <p className="text-xs text-gray-600 mt-1">
                The algo only emits trades when 3+ signals converge. Check back in 20 min — it scans every cycle.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {picks.map((p) => {
              const tierColor = TIER_COLORS[p.tier?.color] || TIER_COLORS.amber;
              const isCall = p.action === "BUY CALLS";
              const Icon = isCall ? TrendingUp : TrendingDown;
              return (
                <Card
                  key={p.pick_id}
                  className={`card-dark border ${tierColor.split(" ")[0]} bg-card hover:border-yellow-500/50 transition-colors`}
                  data-testid={`promise-card-${p.symbol}`}
                >
                  <CardContent className="p-4 space-y-3">
                    {/* Header */}
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xl font-bold text-white">{p.symbol}</span>
                          <Icon className={`w-4 h-4 ${isCall ? "text-emerald-400" : "text-red-400"}`} />
                          <Badge className={`text-[10px] ${isCall ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"} border-0`}>
                            {p.action}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {p.company_name && p.company_name !== p.symbol ? p.company_name : ""}
                        </p>
                      </div>
                      <div className="text-right">
                        <StarRow count={p.tier?.stars || 0} />
                        <p className="text-[10px] uppercase tracking-wide text-purple-300 font-bold mt-1">
                          {p.tier?.label}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          score {p.score}
                        </p>
                      </div>
                    </div>

                    {/* Trade ticket */}
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="bg-black/30 rounded p-2">
                        <p className="text-[10px] text-muted-foreground uppercase">Entry</p>
                        <p className="text-sm font-bold text-white">${p.entry_price}</p>
                      </div>
                      <div className="bg-emerald-500/10 rounded p-2">
                        <p className="text-[10px] text-muted-foreground uppercase">Target</p>
                        <p className="text-sm font-bold text-emerald-400">${p.target_price}</p>
                      </div>
                      <div className="bg-red-500/10 rounded p-2">
                        <p className="text-[10px] text-muted-foreground uppercase">Stop</p>
                        <p className="text-sm font-bold text-red-400">${p.stop_price}</p>
                      </div>
                    </div>

                    {/* Suggested contract */}
                    {p.suggested_contract && (
                      <div className="p-2 bg-purple-500/10 border border-purple-500/30 rounded">
                        <p className="text-[10px] text-purple-300 uppercase font-bold mb-0.5 flex items-center gap-1">
                          <Target className="w-3 h-3" /> Suggested Contract
                        </p>
                        <p className="text-sm font-data font-bold text-white">{p.suggested_contract}</p>
                      </div>
                    )}

                    {/* Risk metrics */}
                    <div className="flex flex-wrap gap-2 text-xs">
                      <span className="px-2 py-0.5 rounded-sm bg-black/40 text-gray-300">
                        R/R <span className="text-yellow-400 font-bold">{p.rr_ratio}x</span>
                      </span>
                      <span className="px-2 py-0.5 rounded-sm bg-black/40 text-gray-300">
                        Move <span className="text-cyan-400 font-bold">{p.expected_move_pct}%</span>
                      </span>
                      <span className="px-2 py-0.5 rounded-sm bg-black/40 text-gray-300">
                        Size <span className="text-pink-400 font-bold">{p.position_size_pct}%</span>
                      </span>
                      <span className="ml-auto text-[10px] text-muted-foreground self-center flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {timeAgo(p.created_at)}
                      </span>
                    </div>

                    {/* Why this trade */}
                    <div className="pt-2 border-t border-gray-700/50 space-y-1">
                      <p className="text-[10px] text-muted-foreground uppercase font-bold">Why this trade</p>
                      {(p.factors || []).map((f, i) => (
                        <p key={i} className="text-xs text-gray-300 leading-snug">{f}</p>
                      ))}
                    </div>

                    {/* Action button */}
                    <button
                      disabled={p.user_taken || takingPick === p.pick_id}
                      onClick={() => handleTakePick(p)}
                      data-testid={`take-pick-${p.symbol}`}
                      className={`w-full py-2 rounded-sm text-sm font-bold transition-colors ${
                        p.user_taken
                          ? "bg-emerald-500/20 text-emerald-300 cursor-default"
                          : "bg-yellow-500/15 hover:bg-yellow-500/25 border border-yellow-500/40 text-yellow-300"
                      }`}
                    >
                      {p.user_taken ? (
                        <span className="flex items-center justify-center gap-1">
                          <Check className="w-4 h-4" /> Taken
                        </span>
                      ) : takingPick === p.pick_id ? (
                        "Marking…"
                      ) : (
                        "I TOOK THIS TRADE"
                      )}
                    </button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent closed picks */}
      {(tr.recent_closed?.length || 0) > 0 && (
        <div>
          <h2 className="font-heading font-bold text-lg tracking-tight mb-3 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-400" />
            RECENT RESULTS
          </h2>
          <Card className="card-dark border-border">
            <CardContent className="p-3">
              <div className="space-y-1">
                {tr.recent_closed.slice(0, 12).map((p) => (
                  <div
                    key={p.pick_id}
                    className="flex items-center gap-3 px-2 py-1.5 rounded-sm bg-black/20 hover:bg-black/40 transition-colors"
                  >
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm ${
                      p.outcome === "WIN" ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"
                    }`}>
                      {p.outcome}
                    </span>
                    <span className="text-sm font-bold text-white w-16">{p.symbol}</span>
                    <span className="text-xs text-muted-foreground flex-1">{p.action}</span>
                    <span className="text-xs text-muted-foreground">${p.entry_price} → ${p.exit_price}</span>
                    <span className={`text-sm font-bold ${(p.pct_return || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {(p.pct_return || 0) >= 0 ? "+" : ""}{p.pct_return}%
                    </span>
                    <span className="text-[10px] text-muted-foreground">{p.age_days_at_close}d</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Disclaimer */}
      <Card className="card-dark border-yellow-500/20 bg-yellow-500/5">
        <CardContent className="p-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-yellow-200/80 leading-relaxed">
            <strong className="text-yellow-300">Past performance ≠ future results.</strong> These are
            algorithm-generated paper picks for tracking; not personalized financial advice.
            Position sizing, stop discipline, and your own due diligence are non-negotiable.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
